import React, { useState, useEffect } from 'react';

const WEEKDAY_LABELS = [
  ['monday', 'Monday'],
  ['tuesday', 'Tuesday'],
  ['wednesday', 'Wednesday'],
  ['thursday', 'Thursday'],
  ['friday', 'Friday'],
  ['saturday', 'Saturday'],
  ['sunday', 'Sunday'],
];
import Layout from '../components/Layout';
import IntegrationsPage from './IntegrationsPage';
import { useAuth } from '../contexts/AuthContext';
import {
  getOrganizations,
  createOrganization,
  switchOrganization,
  getTwilioConfig,
  createTwilioConfig,
  getApiKeys,
  createApiKey,
  deleteApiKey,
  formatApiError,
  createProviderConnection,
  getProviderConnections,
  startGoogleCalendarConnection,
  getOrganizationSchedulingSettings,
  updateOrganizationSchedulingSettings,
  previewSchedulingAvailability,
  getSchedulingBookings,
  getSchedulingBooking,
  getRuntimeSecrets,
  createRuntimeSecret,
  deleteRuntimeSecret,
} from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from '../components/ui/sonner';
import { Building2, Phone, Plus, Check, Loader2, AlertCircle, Key, Trash2, Link2, Calendar, Clock, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const SettingsPage = () => {
  const addDayRange = (dayKey) => {
    setSchedulingForm((prev) => {
      const day = prev.weekly_availability?.[dayKey] || { enabled: true, ranges: [] };
      return {
        ...prev,
        weekly_availability: {
          ...(prev.weekly_availability || {}),
          [dayKey]: {
            ...day,
            enabled: true,
            ranges: [...(day.ranges || []), { start: '09:00', end: '17:00' }],
          },
        },
      };
    });
  };

  const removeDayRange = (dayKey, index) => {
    setSchedulingForm((prev) => {
      const day = prev.weekly_availability?.[dayKey] || { enabled: true, ranges: [] };
      const nextRanges = (day.ranges || []).filter((_, i) => i !== index);
      return {
        ...prev,
        weekly_availability: {
          ...(prev.weekly_availability || {}),
          [dayKey]: {
            ...day,
            ranges: nextRanges.length > 0 ? nextRanges : [{ start: '09:00', end: '17:00' }],
          },
        },
      };
    });
  };

  const addDateOverride = () => {
    setSchedulingForm((prev) => ({
      ...prev,
      date_overrides: [
        ...(prev.date_overrides || []),
        { date: '', enabled: false, label: '', ranges: [{ start: '09:00', end: '17:00' }] },
      ],
    }));
  };

  const removeDateOverride = (index) => {
    setSchedulingForm((prev) => ({
      ...prev,
      date_overrides: (prev.date_overrides || []).filter((_, i) => i !== index),
    }));
  };

  const addOverrideRange = (index) => {
    setSchedulingForm((prev) => ({
      ...prev,
      date_overrides: (prev.date_overrides || []).map((item, i) => i === index
        ? { ...item, enabled: true, ranges: [...(item.ranges || []), { start: '09:00', end: '17:00' }] }
        : item),
    }));
  };

  const removeOverrideRange = (index, rangeIndex) => {
    setSchedulingForm((prev) => ({
      ...prev,
      date_overrides: (prev.date_overrides || []).map((item, i) => {
        if (i !== index) return item;
        const nextRanges = (item.ranges || []).filter((_, rIdx) => rIdx !== rangeIndex);
        return { ...item, ranges: nextRanges.length > 0 ? nextRanges : [{ start: '09:00', end: '17:00' }] };
      }),
    }));
  };
  const { user, refreshUser } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [twilioConfig, setTwilioConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [orgLoading, setOrgLoading] = useState(false);

  // Twilio form
  const [twilioForm, setTwilioForm] = useState({
    account_sid: '',
    auth_token: '',
    phone_number: '',
    a2p_brand_id: '',
    a2p_campaign_id: ''
  });
  const [twilioSaving, setTwilioSaving] = useState(false);
  const [twilioError, setTwilioError] = useState('');

  // Integrations
  const [providerConnections, setProviderConnections] = useState([]);
  const [providerConnectionsLoading, setProviderConnectionsLoading] = useState(true);
  const [providerConnectionsError, setProviderConnectionsError] = useState('');
  const [googleAccountLabel, setGoogleAccountLabel] = useState('');
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [openAIConnectionForm, setOpenAIConnectionForm] = useState({
    name: 'OpenAI Connected Account',
    account_identifier: '',
    auth_token: '',
    refresh_token: '',
  });
  const [savingOpenAIConnection, setSavingOpenAIConnection] = useState(false);

  // Scheduling
  const [schedulingForm, setSchedulingForm] = useState({
    timezone: 'America/New_York',
    booking_url: '',
    scheduling_provider: 'booking_link',
    booking_mode: 'link_only',
    provider_connection_id: '',
    event_type_id: '',
    slot_duration_minutes: 30,
    slot_increment_minutes: 15,
    minimum_notice_minutes: 120,
    buffer_before_minutes: 15,
    buffer_after_minutes: 15,
    booking_window_days: 30,
    business_hours_enabled: true,
    business_hours: { start: '09:00', end: '17:00', timezone: 'America/New_York' },
    weekly_availability: {
      monday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }], timezone: 'America/New_York' },
      tuesday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }], timezone: 'America/New_York' },
      wednesday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }], timezone: 'America/New_York' },
      thursday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }], timezone: 'America/New_York' },
      friday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }], timezone: 'America/New_York' },
      saturday: { enabled: false, ranges: [{ start: '09:00', end: '17:00' }], timezone: 'America/New_York' },
      sunday: { enabled: false, ranges: [{ start: '09:00', end: '17:00' }], timezone: 'America/New_York' },
    },
    date_overrides: [],
  });
  const [schedulingSaving, setSchedulingSaving] = useState(false);
  const [schedulingError, setSchedulingError] = useState('');
  const [schedulingPreview, setSchedulingPreview] = useState(null);
  const [previewingScheduling, setPreviewingScheduling] = useState(false);
  const [bookingRecords, setBookingRecords] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingRecordsLoading, setBookingRecordsLoading] = useState(false);

  // API keys
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(true);
  const [apiDialogOpen, setApiDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [apiSaving, setApiSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const [apiForm, setApiForm] = useState({ provider: 'openai', key: '', name: '' });

  // Custom tool secrets
  const [customSecrets, setCustomSecrets] = useState([]);
  const [customSecretsLoading, setCustomSecretsLoading] = useState(true);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [secretDeleteDialogOpen, setSecretDeleteDialogOpen] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState(null);
  const [secretSaving, setSecretSaving] = useState(false);
  const [secretError, setSecretError] = useState('');
  const [secretForm, setSecretForm] = useState({ name: '', value: '' });

  const fetchApiKeysData = async () => {
    try {
      const { data } = await getApiKeys();
      setApiKeys(data);
    } catch (e) {
      console.error('Failed to fetch API keys:', e);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const fetchCustomSecrets = async () => {
    try {
      const { data } = await getRuntimeSecrets();
      setCustomSecrets((data || []).filter((s) => s.metadata?.source === 'custom_tool_secret'));
    } catch (e) {
      console.error('Failed to fetch custom secrets:', e);
    } finally {
      setCustomSecretsLoading(false);
    }
  };

  const handleSaveSecret = async (e) => {
    e.preventDefault();
    setSecretError('');
    const varName = secretForm.name.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    if (!varName || !/^[A-Z_]/.test(varName)) {
      setSecretError('Variable name must start with a letter or underscore.');
      return;
    }
    if (!secretForm.value.trim()) {
      setSecretError('Secret value is required.');
      return;
    }
    setSecretSaving(true);
    try {
      await createRuntimeSecret({
        name: varName,
        secret_kind: 'api_key',
        secret_value: secretForm.value,
        reference_key: varName,
        metadata: { source: 'custom_tool_secret' },
      });
      await fetchCustomSecrets();
      setSecretDialogOpen(false);
      setSecretForm({ name: '', value: '' });
      toast.success('Secret saved');
    } catch (e) {
      setSecretError(formatApiError(e));
    } finally {
      setSecretSaving(false);
    }
  };

  const handleDeleteSecret = async () => {
    if (!secretToDelete) return;
    try {
      await deleteRuntimeSecret(secretToDelete.id);
      setCustomSecrets(customSecrets.filter((s) => s.id !== secretToDelete.id));
      toast.success('Secret deleted');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSecretDeleteDialogOpen(false);
      setSecretToDelete(null);
    }
  };

  const fetchProviderConnectionsData = async () => {
    setProviderConnectionsLoading(true);
    setProviderConnectionsError('');
    try {
      const { data } = await getProviderConnections();
      setProviderConnections(data || []);
    } catch (e) {
      console.error('Failed to fetch provider connections:', e);
      setProviderConnectionsError(formatApiError(e));
    } finally {
      setProviderConnectionsLoading(false);
    }
  };

  const fetchSchedulingSettingsData = async () => {
    try {
      const { data } = await getOrganizationSchedulingSettings();
      if (data?.settings) setSchedulingForm(data.settings);
    } catch (e) {
      console.error('Failed to fetch scheduling settings:', e);
      setSchedulingError(formatApiError(e));
    }
  };

  const fetchBookingRecords = async () => {
    setBookingRecordsLoading(true);
    try {
      const { data } = await getSchedulingBookings({ limit: 50 });
      setBookingRecords(data || []);
    } catch (e) {
      console.error('Failed to fetch booking records:', e);
    } finally {
      setBookingRecordsLoading(false);
    }
  };

  const openBookingDetail = async (bookingReference) => {
    try {
      const { data } = await getSchedulingBooking(bookingReference);
      setSelectedBooking(data);
    } catch (e) {
      console.error('Failed to fetch booking detail:', e);
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('googleCalendarConnected') === '1') {
      toast.success('Google Calendar connected successfully');
      params.delete('googleCalendarConnected');
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', next);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgsRes, twilioRes, providerConnectionsRes, schedulingRes, bookingsRes] = await Promise.all([
          getOrganizations(),
          getTwilioConfig(),
          getProviderConnections(),
          getOrganizationSchedulingSettings(),
          getSchedulingBookings({ limit: 50 })
        ]);
        setOrganizations(orgsRes.data);
        setTwilioConfig(twilioRes.data);
        setProviderConnections(providerConnectionsRes.data || []);
        if (schedulingRes.data?.settings) setSchedulingForm(schedulingRes.data.settings);
        setBookingRecords(bookingsRes.data || []);
      } catch (e) {
        console.error('Failed to fetch settings:', e);
        setProviderConnectionsError(formatApiError(e));
      } finally {
        setLoading(false);
        setProviderConnectionsLoading(false);
      }
    };
    fetchData();
    fetchApiKeysData();
    fetchCustomSecrets();
  }, []);

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    setOrgLoading(true);
    try {
      await createOrganization(newOrgName);
      await refreshUser();
      const { data } = await getOrganizations();
      setOrganizations(data);
      setOrgDialogOpen(false);
      setNewOrgName('');
      toast.success('Organization created');
    } catch (e) {
      console.error('Failed to create org:', e);
      toast.error(formatApiError(e));
    } finally {
      setOrgLoading(false);
    }
  };

  const handleSwitchOrg = async (orgId) => {
    try {
      await switchOrganization(orgId);
      await refreshUser();
      toast.success('Organization switched');
      await Promise.all([fetchProviderConnectionsData(), fetchSchedulingSettingsData()]);
    } catch (e) {
      console.error('Failed to switch org:', e);
      toast.error(formatApiError(e));
    }
  };

  const handleSaveTwilio = async (e) => {
    e.preventDefault();
    setTwilioSaving(true);
    setTwilioError('');
    try {
      const { data } = await createTwilioConfig(twilioForm);
      setTwilioConfig(data);
      setTwilioForm({
        account_sid: '',
        auth_token: '',
        phone_number: '',
        a2p_brand_id: '',
        a2p_campaign_id: ''
      });
      toast.success('Twilio configuration saved');
    } catch (e) {
      setTwilioError(formatApiError(e));
    } finally {
      setTwilioSaving(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    setConnectingGoogle(true);
    try {
      const res = await startGoogleCalendarConnection({
        name: googleAccountLabel?.trim() || 'Google Calendar'
      });
      const url = res.data?.authorization_url;
      if (!url) throw new Error('No Google authorization URL returned');
      window.location.href = url;
    } catch (e) {
      console.error('Failed to start Google Calendar connection:', e);
      toast.error(formatApiError(e));
      setConnectingGoogle(false);
    }
  };

  const handleSaveOpenAIConnection = async () => {
    setSavingOpenAIConnection(true);
    setProviderConnectionsError('');
    try {
      await createProviderConnection({
        provider: 'openai_connected_account',
        name: openAIConnectionForm.name?.trim() || 'OpenAI Connected Account',
        account_identifier: openAIConnectionForm.account_identifier?.trim() || null,
        status: 'active',
        connection_config: {
          auth_mode: 'manual_auth_token',
          auth_token: openAIConnectionForm.auth_token?.trim(),
          refresh_token: openAIConnectionForm.refresh_token?.trim() || null,
        },
      });
      toast.success('OpenAI connected-account token saved.');
      setOpenAIConnectionForm((prev) => ({ ...prev, auth_token: '', refresh_token: '' }));
      await fetchProviderConnectionsData();
    } catch (e) {
      console.error('Failed to save OpenAI connected account token:', e);
      setProviderConnectionsError(formatApiError(e));
    } finally {
      setSavingOpenAIConnection(false);
    }
  };

  const buildSchedulingPayload = () => ({
    ...schedulingForm,
    business_hours: {
      ...(schedulingForm.business_hours || {}),
      timezone: schedulingForm.timezone,
    },
    weekly_availability: Object.fromEntries(
      Object.entries(schedulingForm.weekly_availability || {}).map(([day, entry]) => [day, {
        ...(entry || {}),
        timezone: schedulingForm.timezone,
      }])
    ),
    date_overrides: (schedulingForm.date_overrides || []).map((entry) => ({
      ...(entry || {}),
      timezone: schedulingForm.timezone,
    })),
  });

  const handlePreviewScheduling = async () => {
    setPreviewingScheduling(true);
    try {
      const { data } = await previewSchedulingAvailability({
        provider_connection_id: schedulingForm.provider_connection_id || null,
        category_settings: buildSchedulingPayload(),
      });
      setSchedulingPreview(data);
      toast.success(`Found ${data.slot_count || 0} preview slots`);
    } catch (e) {
      console.error('Failed to preview scheduling settings:', e);
      setSchedulingError(formatApiError(e));
    } finally {
      setPreviewingScheduling(false);
    }
  };

  const applyHolidayPreset = (year = new Date().getFullYear()) => {
    const presets = [
      { date: `${year}-01-01`, label: `New Year's Day ${year}`, enabled: false, ranges: [{ start: '09:00', end: '17:00' }] },
      { date: `${year}-07-04`, label: `Independence Day ${year}`, enabled: false, ranges: [{ start: '09:00', end: '17:00' }] },
      { date: `${year}-12-25`, label: `Christmas Day ${year}`, enabled: false, ranges: [{ start: '09:00', end: '17:00' }] },
    ];
    setSchedulingForm((prev) => {
      const existing = new Set((prev.date_overrides || []).map((x) => x.date));
      return {
        ...prev,
        date_overrides: [...(prev.date_overrides || []), ...presets.filter((p) => !existing.has(p.date))],
      };
    });
    toast.success(`Added holiday blackout presets for ${year}`);
  };

  const handleSaveScheduling = async (e) => {
    e.preventDefault();
    setSchedulingSaving(true);
    setSchedulingError('');
    try {
      const payload = buildSchedulingPayload();
      const { data } = await updateOrganizationSchedulingSettings(payload);
      setSchedulingForm(data.settings);
      toast.success('Scheduling defaults saved');
    } catch (e) {
      console.error('Failed to save scheduling settings:', e);
      setSchedulingError(formatApiError(e));
    } finally {
      setSchedulingSaving(false);
    }
  };

  const providers = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'gemini', label: 'Google Gemini' },
  ];

  const getProviderColor = (provider) => {
    const colors = {
      openai: '#10B981',
      anthropic: '#8B5CF6',
      gemini: '#F59E0B'
    };
    return colors[provider] || '#71717A';
  };

  const handleSaveApiKey = async (e) => {
    e.preventDefault();
    setApiError('');
    setApiSaving(true);
    try {
      await createApiKey(apiForm);
      await fetchApiKeysData();
      setApiDialogOpen(false);
      setApiForm({ provider: 'openai', key: '', name: '' });
      toast.success('API key added');
    } catch (e) {
      setApiError(formatApiError(e));
    } finally {
      setApiSaving(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!keyToDelete) return;
    try {
      await deleteApiKey(keyToDelete.id);
      setApiKeys(apiKeys.filter(k => k.id !== keyToDelete.id));
      toast.success('API key deleted');
    } catch (e) {
      console.error('Failed to delete API key:', e);
      toast.error(formatApiError(e));
    } finally {
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
    }
  };

  const getConnectionStatusClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'active':
        return 'text-[#22C55E]';
      case 'inactive':
        return 'text-muted-foreground';
      case 'error':
        return 'text-destructive';
      default:
        return 'text-[#F59E0B]';
    }
  };

  const googleConnections = providerConnections.filter(c => c.provider === 'google_calendar');
  const openAIConnections = providerConnections.filter(c => c.provider === 'openai_connected_account');

  return (
    <Layout>
      <div className="p-8 max-w-4xl" data-testid="settings-page">
        <h1 className="text-3xl font-semibold tracking-tight mb-2 font-['Outfit']">Settings</h1>
        <p className="text-muted-foreground mb-8">Manage your organization, integrations, and deployment surfaces.</p>

        <Tabs defaultValue="organization">
          <TabsList className="mb-6 flex flex-wrap h-auto">
            <TabsTrigger value="organization" className="gap-2">
              <Building2 className="w-4 h-4" />
              Organization
            </TabsTrigger>
            <TabsTrigger value="integrations" className="gap-2" data-testid="settings-integrations-tab">
              <Link2 className="w-4 h-4" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="scheduling" className="gap-2" data-testid="settings-scheduling-tab">
              <Clock className="w-4 h-4" />
              Scheduling
            </TabsTrigger>
            <TabsTrigger value="bookings" className="gap-2" data-testid="settings-bookings-tab">
              <Calendar className="w-4 h-4" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="sms" className="gap-2">
              <Phone className="w-4 h-4" />
              SMS Deployment
            </TabsTrigger>
            <TabsTrigger value="api-keys" className="gap-2">
              <Key className="w-4 h-4" />
              API Keys
            </TabsTrigger>
          </TabsList>

          <TabsContent value="organization" className="space-y-6">
            <div className="apollo-panel p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold font-['Outfit']">Your Organizations</h2>
                <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-sm gap-2" data-testid="create-org-button">
                      <Plus className="w-4 h-4" />
                      New Organization
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Organization</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Organization Name</Label>
                        <Input
                          value={newOrgName}
                          onChange={(e) => setNewOrgName(e.target.value)}
                          placeholder="Acme Inc."
                          className="rounded-sm"
                          data-testid="new-org-name-input"
                        />
                      </div>
                      <Button 
                        onClick={handleCreateOrg}
                        disabled={orgLoading || !newOrgName.trim()}
                        className="apollo-primary-button w-full rounded-sm"
                        data-testid="submit-create-org"
                      >
                        {orgLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {loading ? (
                <div className="text-center py-4 text-muted-foreground">Loading...</div>
              ) : organizations.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No organizations yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {organizations.map((org) => (
                    <div 
                      key={org.id}
                      className={`flex items-center justify-between rounded-sm border p-4 transition-colors ${
                        user?.organization_id === org.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border bg-background/40 hover:bg-secondary/30'
                      }`}
                      data-testid={`org-item-${org.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="apollo-icon-tile w-10 h-10 rounded-sm flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{org.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Created {new Date(org.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {user?.organization_id === org.id ? (
                        <span className="flex items-center gap-1 text-sm text-primary">
                          <Check className="w-4 h-4" /> Active
                        </span>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSwitchOrg(org.id)}
                          className="rounded-sm"
                          data-testid={`switch-org-${org.id}`}
                        >
                          Switch
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="integrations" className="space-y-6">
            <IntegrationsPage embedded />
          </TabsContent>
          <TabsContent value="scheduling" className="space-y-6">
            <div className="apollo-panel p-6">
              <h2 className="font-semibold font-['Outfit'] mb-2">Organization Scheduling Defaults</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Set default availability and booking rules for all appointment-setting agents in this organization. 
                Agents can optionally override these defaults when they need custom behavior.
              </p>

              <form onSubmit={handleSaveScheduling} className="space-y-6">
                {schedulingError && (
                  <div className="flex items-center gap-2 rounded-sm border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive">
                    <AlertCircle className="w-4 h-4" />
                    {schedulingError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Select 
                      value={schedulingForm.timezone} 
                      onValueChange={(v) => setSchedulingForm({...schedulingForm, timezone: v})}
                    >
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney', 'UTC'].map(tz => (
                          <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Default Provider</Label>
                    <Select 
                      value={schedulingForm.scheduling_provider} 
                      onValueChange={(v) => setSchedulingForm({...schedulingForm, scheduling_provider: v})}
                    >
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="booking_link">Booking Link Only</SelectItem>
                        <SelectItem value="google_calendar">Google Calendar</SelectItem>
                        <SelectItem value="calendly">Calendly</SelectItem>
                        <SelectItem value="cal_com">Cal.com</SelectItem>
                        <SelectItem value="highlevel">HighLevel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Booking Mode</Label>
                    <Select 
                      value={schedulingForm.booking_mode} 
                      onValueChange={(v) => setSchedulingForm({...schedulingForm, booking_mode: v})}
                    >
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="link_only">Share Booking Link</SelectItem>
                        <SelectItem value="availability_lookup">Show Live Availability</SelectItem>
                        <SelectItem value="direct_booking">Book Directly In Chat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Default Provider Connection</Label>
                    <Select 
                      value={schedulingForm.provider_connection_id || '__none__'} 
                      onValueChange={(v) => setSchedulingForm({...schedulingForm, provider_connection_id: v === '__none__' ? '' : v})}
                    >
                      <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select a connection" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No default</SelectItem>
                        {providerConnections.filter(c => c.provider === schedulingForm.scheduling_provider).map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Default Booking URL</Label>
                  <Input
                    value={schedulingForm.booking_url || ''}
                    onChange={(e) => setSchedulingForm({...schedulingForm, booking_url: e.target.value})}
                    placeholder="https://calendly.com/your-link"
                    className="rounded-sm"
                  />
                </div>

                <div className="border border-border rounded-sm p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Availability Rules</Label>
                      <p className="text-xs text-muted-foreground">Choose which days are bookable and set separate hours for each day.</p>
                    </div>
                    <Switch 
                      checked={schedulingForm.business_hours_enabled} 
                      onCheckedChange={(v) => setSchedulingForm({...schedulingForm, business_hours_enabled: v})}
                    />
                  </div>
                  {schedulingForm.business_hours_enabled && (
                    <div className="space-y-3">
                      {WEEKDAY_LABELS.map(([dayKey, dayLabel]) => {
                        const day = schedulingForm.weekly_availability?.[dayKey] || { enabled: false, ranges: [{ start: '09:00', end: '17:00' }] };
                        return (
                          <div key={dayKey} className="border border-border/60 rounded-sm p-3 bg-background/40 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-medium text-sm">{dayLabel}</div>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={!!day.enabled}
                                  onCheckedChange={(v) => setSchedulingForm({
                                    ...schedulingForm,
                                    weekly_availability: {
                                      ...(schedulingForm.weekly_availability || {}),
                                      [dayKey]: { ...day, enabled: v }
                                    }
                                  })}
                                />
                                <span className="text-xs text-muted-foreground">{day.enabled ? 'On' : 'Off'}</span>
                              </div>
                            </div>

                            <div className="space-y-2">
                              {(day.ranges || []).map((range, rangeIndex) => (
                                <div key={`${dayKey}-${rangeIndex}`} className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Start</Label>
                                    <Input
                                      type="time"
                                      value={range.start || '09:00'}
                                      disabled={!day.enabled}
                                      onChange={(e) => setSchedulingForm({
                                        ...schedulingForm,
                                        weekly_availability: {
                                          ...(schedulingForm.weekly_availability || {}),
                                          [dayKey]: {
                                            ...day,
                                            ranges: (day.ranges || []).map((r, i) => i === rangeIndex ? { ...r, start: e.target.value } : r)
                                          }
                                        }
                                      })}
                                      className="rounded-sm"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">End</Label>
                                    <Input
                                      type="time"
                                      value={range.end || '17:00'}
                                      disabled={!day.enabled}
                                      onChange={(e) => setSchedulingForm({
                                        ...schedulingForm,
                                        weekly_availability: {
                                          ...(schedulingForm.weekly_availability || {}),
                                          [dayKey]: {
                                            ...day,
                                            ranges: (day.ranges || []).map((r, i) => i === rangeIndex ? { ...r, end: e.target.value } : r)
                                          }
                                        }
                                      })}
                                      className="rounded-sm"
                                    />
                                  </div>
                                  <Button type="button" variant="ghost" size="icon" disabled={!day.enabled || (day.ranges || []).length <= 1} onClick={() => removeDayRange(dayKey, rangeIndex)}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button type="button" variant="outline" size="sm" onClick={() => addDayRange(dayKey)} disabled={!day.enabled} className="rounded-sm gap-2">
                                <Plus className="w-4 h-4" /> Add time block
                              </Button>
                            </div>
                          </div>
                        );
                      })}

                      <div className="border border-border rounded-sm p-4 space-y-4 bg-background/30">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <Label>Date Overrides / Blackout Dates</Label>
                            <p className="text-xs text-muted-foreground">Override the weekly schedule for a specific date, or mark a date unavailable.</p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button type="button" variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => applyHolidayPreset(new Date().getFullYear())}>
                              <Calendar className="w-4 h-4" /> Add holiday presets
                            </Button>
                            <Button type="button" variant="outline" size="sm" className="rounded-sm gap-2" onClick={addDateOverride}>
                              <Plus className="w-4 h-4" /> Add override
                            </Button>
                          </div>
                        </div>

                        {(schedulingForm.date_overrides || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground">No date-specific overrides yet.</p>
                        ) : (
                          <div className="space-y-3">
                            {(schedulingForm.date_overrides || []).map((override, overrideIndex) => (
                              <div key={`override-${overrideIndex}`} className="border border-border/60 rounded-sm p-3 space-y-3 bg-background/40">
                                <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto,auto] gap-3 items-end">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Date</Label>
                                    <Input type="date" value={override.date || ''} onChange={(e) => setSchedulingForm({
                                      ...schedulingForm,
                                      date_overrides: (schedulingForm.date_overrides || []).map((item, i) => i === overrideIndex ? { ...item, date: e.target.value } : item)
                                    })} className="rounded-sm" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Label</Label>
                                    <Input value={override.label || ''} onChange={(e) => setSchedulingForm({
                                      ...schedulingForm,
                                      date_overrides: (schedulingForm.date_overrides || []).map((item, i) => i === overrideIndex ? { ...item, label: e.target.value } : item)
                                    })} placeholder="Holiday, special hours, etc." className="rounded-sm" />
                                  </div>
                                  <div className="flex items-center gap-2 pb-2">
                                    <Switch checked={!!override.enabled} onCheckedChange={(v) => setSchedulingForm({
                                      ...schedulingForm,
                                      date_overrides: (schedulingForm.date_overrides || []).map((item, i) => i === overrideIndex ? { ...item, enabled: v } : item)
                                    })} />
                                    <span className="text-xs text-muted-foreground">{override.enabled ? 'Available' : 'Blocked'}</span>
                                  </div>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => removeDateOverride(overrideIndex)}>
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </div>

                                {override.enabled && (
                                  <div className="space-y-2">
                                    {(override.ranges || []).map((range, rangeIndex) => (
                                      <div key={`override-${overrideIndex}-range-${rangeIndex}`} className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end">
                                        <div className="space-y-1">
                                          <Label className="text-xs">Start</Label>
                                          <Input type="time" value={range.start || '09:00'} onChange={(e) => setSchedulingForm({
                                            ...schedulingForm,
                                            date_overrides: (schedulingForm.date_overrides || []).map((item, i) => i === overrideIndex ? {
                                              ...item,
                                              ranges: (item.ranges || []).map((r, rIdx) => rIdx === rangeIndex ? { ...r, start: e.target.value } : r)
                                            } : item)
                                          })} className="rounded-sm" />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs">End</Label>
                                          <Input type="time" value={range.end || '17:00'} onChange={(e) => setSchedulingForm({
                                            ...schedulingForm,
                                            date_overrides: (schedulingForm.date_overrides || []).map((item, i) => i === overrideIndex ? {
                                              ...item,
                                              ranges: (item.ranges || []).map((r, rIdx) => rIdx === rangeIndex ? { ...r, end: e.target.value } : r)
                                            } : item)
                                          })} className="rounded-sm" />
                                        </div>
                                        <Button type="button" variant="ghost" size="icon" disabled={(override.ranges || []).length <= 1} onClick={() => removeOverrideRange(overrideIndex, rangeIndex)}>
                                          <X className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    ))}
                                    <Button type="button" variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => addOverrideRange(overrideIndex)}>
                                      <Plus className="w-4 h-4" /> Add override time block
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Slot Duration (min)</Label>
                          <Input 
                            type="number" min="5" step="5" 
                            value={schedulingForm.slot_duration_minutes || 30} 
                            onChange={(e) => setSchedulingForm({...schedulingForm, slot_duration_minutes: Number(e.target.value) || 30})}
                            className="rounded-sm" 
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Slot Increment (min)</Label>
                          <Input 
                            type="number" min="5" step="5" 
                            value={schedulingForm.slot_increment_minutes || 15} 
                            onChange={(e) => setSchedulingForm({...schedulingForm, slot_increment_minutes: Number(e.target.value) || 15})}
                            className="rounded-sm" 
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Minimum Notice (min)</Label>
                          <Input 
                            type="number" min="0" step="5" 
                            value={schedulingForm.minimum_notice_minutes || 120} 
                            onChange={(e) => setSchedulingForm({...schedulingForm, minimum_notice_minutes: Number(e.target.value) || 0})}
                            className="rounded-sm" 
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Booking Window (days)</Label>
                          <Input 
                            type="number" min="1" step="1" 
                            value={schedulingForm.booking_window_days || 30} 
                            onChange={(e) => setSchedulingForm({...schedulingForm, booking_window_days: Number(e.target.value) || 30})}
                            className="rounded-sm" 
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Buffer Before (min)</Label>
                          <Input 
                            type="number" min="0" step="5" 
                            value={schedulingForm.buffer_before_minutes || 15} 
                            onChange={(e) => setSchedulingForm({...schedulingForm, buffer_before_minutes: Number(e.target.value) || 0})}
                            className="rounded-sm" 
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Buffer After (min)</Label>
                          <Input 
                            type="number" min="0" step="5" 
                            value={schedulingForm.buffer_after_minutes || 15} 
                            onChange={(e) => setSchedulingForm({...schedulingForm, buffer_after_minutes: Number(e.target.value) || 0})}
                            className="rounded-sm" 
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <Button type="button" variant="outline" onClick={handlePreviewScheduling} disabled={previewingScheduling} className="rounded-sm">
                    {previewingScheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                    Preview Availability
                  </Button>
                  <Button type="submit" disabled={schedulingSaving} className="apollo-primary-button rounded-sm">
                    {schedulingSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Scheduling Defaults
                  </Button>
                </div>

                {schedulingPreview && (
                  <div className="border rounded-sm p-4 bg-background/40 space-y-3">
                    <div className="text-sm font-medium">Availability Preview</div>
                    <div className="text-xs text-muted-foreground">Provider: {schedulingPreview.provider} · Mode: {schedulingPreview.mode} · Timezone: {schedulingPreview.timezone}</div>
                    <div className="space-y-2">
                      {(schedulingPreview.slots || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No slots available with the current rules.</p>
                      ) : (
                        schedulingPreview.slots.map((slot, idx) => (
                          <div key={`${slot.start}-${idx}`} className="text-sm rounded-sm border px-3 py-2 bg-background/80">{slot.display}</div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </form>
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="space-y-6">
            <div className="apollo-panel p-6 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-semibold font-['Outfit']">Booking Management</h2>
                  <p className="text-sm text-muted-foreground mt-1">Inspect booking records, lifecycle status, and action history for recovery/debugging.</p>
                </div>
                <Button variant="outline" className="rounded-sm" onClick={fetchBookingRecords} disabled={bookingRecordsLoading}>
                  {bookingRecordsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </Button>
              </div>

              {bookingRecords.length === 0 ? (
                <p className="text-sm text-muted-foreground">No booking records yet.</p>
              ) : (
                <div className="space-y-3">
                  {bookingRecords.map((booking) => (
                    <button key={booking.booking_reference} type="button" className="w-full text-left border rounded-sm p-4 bg-background/40 hover:bg-background/70 transition-colors" onClick={() => openBookingDetail(booking.booking_reference)}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium text-sm">{booking.booking_reference}</div>
                          <div className="text-xs text-muted-foreground mt-1">{booking.provider} · {booking.contact_name || booking.contact_email || 'Unknown contact'}</div>
                          <div className="text-xs text-muted-foreground">{booking.summary || 'No summary'}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm capitalize">{booking.status}</div>
                          <div className="text-xs text-muted-foreground">{booking.start_at ? new Date(booking.start_at).toLocaleString() : 'No start time'}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {selectedBooking && (
                <div className="border rounded-sm p-4 space-y-3 bg-background/30">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium">{selectedBooking.booking_reference}</div>
                      <div className="text-xs text-muted-foreground">{selectedBooking.provider} · {selectedBooking.status}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedBooking(null)}>Close</Button>
                  </div>
                  <div className="text-sm space-y-1">
                    <div><strong>Contact:</strong> {selectedBooking.contact_name || selectedBooking.contact_email || selectedBooking.contact_phone || 'Unknown'}</div>
                    <div><strong>Summary:</strong> {selectedBooking.summary || '—'}</div>
                    <div><strong>Time:</strong> {selectedBooking.start_at ? new Date(selectedBooking.start_at).toLocaleString() : '—'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium mb-2">Action History</div>
                    <div className="space-y-2">
                      {(selectedBooking.action_history || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No action history recorded.</p>
                      ) : (
                        selectedBooking.action_history.map((action, idx) => (
                          <div key={idx} className="border rounded-sm p-3 bg-background/70">
                            <div className="text-sm font-medium capitalize">{action.action} · {action.status}</div>
                            <div className="text-xs text-muted-foreground">{action.timestamp ? new Date(action.timestamp).toLocaleString() : 'Unknown time'}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sms" className="space-y-6">
            <div className="apollo-panel p-6">
              <h2 className="font-semibold font-['Outfit'] mb-4">SMS Deployment (Twilio)</h2>
              
              <p className="text-sm text-muted-foreground mb-4">Connect Twilio here when you want the same Apollo agent brains to handle SMS conversations in addition to web chat.</p>
              {twilioConfig ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-secondary/60 rounded-sm">
                    <Phone className="w-5 h-5 text-[#22C55E]" />
                    <div>
                      <p className="font-medium">{twilioConfig.phone_number}</p>
                      <p className="text-sm text-muted-foreground">
                        A2P Status: <span className="capitalize">{twilioConfig.a2p_status}</span>
                      </p>
                    </div>
                    <span className={`ml-auto status-dot ${twilioConfig.is_verified ? 'active' : 'pending'}`}></span>
                  </div>
                  
                  <div className="p-4 border border-border rounded-sm">
                    <h3 className="font-medium mb-2">A2P 10DLC Setup</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      To send SMS in the US, you need to register for A2P 10DLC. This involves:
                    </p>
                    <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                      <li>Register your business brand with The Campaign Registry</li>
                      <li>Create a campaign describing your use case</li>
                      <li>Associate your phone number with the campaign</li>
                    </ol>
                    <a 
                      href="https://www.twilio.com/docs/messaging/compliance/a2p-10dlc" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 hover:underline text-sm mt-3 inline-block"
                    >
                      Learn more about A2P 10DLC →
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSaveTwilio} className="space-y-4">
                  {twilioError && (
                    <div className="flex items-center gap-2 rounded-sm border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive">
                      <AlertCircle className="w-4 h-4" />
                      {twilioError}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Account SID</Label>
                      <Input
                        value={twilioForm.account_sid}
                        onChange={(e) => setTwilioForm({...twilioForm, account_sid: e.target.value})}
                        placeholder="ACxxxxxxxxxxxxxxxx"
                        className="rounded-sm"
                        required
                        data-testid="twilio-account-sid"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Auth Token</Label>
                      <Input
                        type="password"
                        value={twilioForm.auth_token}
                        onChange={(e) => setTwilioForm({...twilioForm, auth_token: e.target.value})}
                        placeholder="••••••••••••••••"
                        className="rounded-sm"
                        required
                        data-testid="twilio-auth-token"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      value={twilioForm.phone_number}
                      onChange={(e) => setTwilioForm({...twilioForm, phone_number: e.target.value})}
                      placeholder="+1234567890"
                      className="rounded-sm"
                      required
                      data-testid="twilio-phone-number"
                    />
                    <p className="text-xs text-muted-foreground">E.164 format with country code</p>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <h3 className="font-medium mb-3">A2P 10DLC (Optional)</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Brand ID</Label>
                        <Input
                          value={twilioForm.a2p_brand_id}
                          onChange={(e) => setTwilioForm({...twilioForm, a2p_brand_id: e.target.value})}
                          placeholder="BNxxxxxxxx"
                          className="rounded-sm"
                          data-testid="twilio-brand-id"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Campaign ID</Label>
                        <Input
                          value={twilioForm.a2p_campaign_id}
                          onChange={(e) => setTwilioForm({...twilioForm, a2p_campaign_id: e.target.value})}
                          placeholder="CMxxxxxxxx"
                          className="rounded-sm"
                          data-testid="twilio-campaign-id"
                        />
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit"
                    disabled={twilioSaving}
                    className="apollo-primary-button rounded-sm"
                    data-testid="save-twilio-button"
                  >
                    {twilioSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Configuration
                  </Button>
                </form>
              )}
            </div>
          </TabsContent>

          <TabsContent value="api-keys" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold font-['Outfit']">API Keys</h2>
                <p className="text-muted-foreground mt-1">Manage your LLM provider API keys</p>
              </div>
              <Dialog open={apiDialogOpen} onOpenChange={setApiDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="apollo-primary-button rounded-sm gap-2" data-testid="add-api-key-button">
                    <Plus className="w-4 h-4" />
                    Add API Key
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add API Key</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveApiKey} className="space-y-4 pt-4">
                    {apiError && (
                      <div className="flex items-center gap-2 rounded-sm border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive">
                        <AlertCircle className="w-4 h-4" />
                        {apiError}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Provider</Label>
                      <Select value={apiForm.provider} onValueChange={(v) => setApiForm({...apiForm, provider: v})}>
                        <SelectTrigger className="rounded-sm" data-testid="provider-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {providers.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Name (Optional)</Label>
                      <Input
                        value={apiForm.name}
                        onChange={(e) => setApiForm({...apiForm, name: e.target.value})}
                        placeholder="e.g., Production Key"
                        className="rounded-sm"
                        data-testid="key-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <Input
                        type="password"
                        value={apiForm.key}
                        onChange={(e) => setApiForm({...apiForm, key: e.target.value})}
                        placeholder="sk-..."
                        className="rounded-sm font-mono"
                        required
                        data-testid="api-key-input"
                      />
                      <p className="text-xs text-muted-foreground">Your key is encrypted and stored securely</p>
                    </div>

                    <Button type="submit" disabled={apiSaving || !apiForm.key} className="apollo-primary-button w-full rounded-sm" data-testid="save-api-key-button">
                      {apiSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Key'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="apollo-panel overflow-hidden">
              {apiKeysLoading ? (
                <div className="p-8 text-center text-muted-foreground">Loading...</div>
              ) : apiKeys.length === 0 ? (
                <div className="p-12 text-center">
                  <Key className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold mb-2 font-['Outfit']">No API keys</h2>
                  <p className="text-muted-foreground mb-4">Add your own LLM provider API keys to use with your agents</p>
                  <p className="text-sm text-muted-foreground">Or use the platform's shared API key (billed to your account)</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {apiKeys.map((key) => (
                    <div key={key.id} className="p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors" data-testid={`api-key-row-${key.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-sm flex items-center justify-center border border-border/60 bg-background/70" style={{ boxShadow: `inset 0 0 0 1px ${getProviderColor(key.provider)}25` }}>
                          <Key className="w-5 h-5" style={{ color: getProviderColor(key.provider) }} />
                        </div>
                        <div>
                          <p className="font-medium">{key.name || `${key.provider} Key`}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full capitalize border border-border/50 bg-background/80" style={{ backgroundColor: `${getProviderColor(key.provider)}15`, color: getProviderColor(key.provider) }}>
                              {key.provider}
                            </span>
                            <span className="text-sm text-muted-foreground font-mono">{key.key_preview}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Added {new Date(key.created_at).toLocaleDateString()}</span>
                        <button onClick={() => { setKeyToDelete(key); setDeleteDialogOpen(true); }} className="p-2 rounded-sm text-destructive transition-colors hover:bg-destructive/10" data-testid={`delete-key-${key.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tool Secrets */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold font-['Outfit']">Tool Secrets</h2>
                <p className="text-muted-foreground mt-1">Store API keys for external tools. Agents access these as environment variables — values are never exposed in prompts or logs.</p>
              </div>
              <Dialog open={secretDialogOpen} onOpenChange={(open) => { setSecretDialogOpen(open); if (!open) { setSecretForm({ name: '', value: '' }); setSecretError(''); } }}>
                <DialogTrigger asChild>
                  <Button className="apollo-primary-button rounded-sm gap-2">
                    <Plus className="w-4 h-4" />
                    Add Secret
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Tool Secret</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveSecret} className="space-y-4 pt-4">
                    {secretError && (
                      <div className="flex items-center gap-2 rounded-sm border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive">
                        <AlertCircle className="w-4 h-4" />
                        {secretError}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Variable Name</Label>
                      <Input
                        value={secretForm.name}
                        onChange={(e) => setSecretForm({ ...secretForm, name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
                        placeholder="MY_API_KEY"
                        className="rounded-sm font-mono"
                        required
                      />
                      <p className="text-xs text-muted-foreground">Agents reference this as <span className="font-mono">$MY_API_KEY</span> in shell commands.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Secret Value</Label>
                      <Input
                        type="password"
                        value={secretForm.value}
                        onChange={(e) => setSecretForm({ ...secretForm, value: e.target.value })}
                        placeholder="Paste your API key here"
                        className="rounded-sm font-mono"
                        required
                      />
                      <p className="text-xs text-muted-foreground">Encrypted at rest. Never included in LLM prompts or logs.</p>
                    </div>
                    <Button type="submit" disabled={secretSaving || !secretForm.name || !secretForm.value} className="apollo-primary-button w-full rounded-sm">
                      {secretSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Secret'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="apollo-panel overflow-hidden">
              {customSecretsLoading ? (
                <div className="p-8 text-center text-muted-foreground">Loading...</div>
              ) : customSecrets.length === 0 ? (
                <div className="p-12 text-center">
                  <Key className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold mb-2 font-['Outfit']">No tool secrets</h2>
                  <p className="text-muted-foreground mb-2">Add API keys for external services your agents need to call.</p>
                  <p className="text-sm text-muted-foreground">Each secret is injected as an environment variable during agent shell execution.</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {customSecrets.map((secret) => (
                    <div key={secret.id} className="p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-sm flex items-center justify-center border border-border/60 bg-background/70">
                          <Key className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium font-mono">{secret.name}</p>
                          <p className="text-sm text-muted-foreground font-mono">{secret.value_preview || '***'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Added {new Date(secret.created_at).toLocaleDateString()}</span>
                        <button
                          onClick={() => { setSecretToDelete(secret); setSecretDeleteDialogOpen(true); }}
                          className="p-2 rounded-sm text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <AlertDialog open={secretDeleteDialogOpen} onOpenChange={setSecretDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete secret?</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-mono font-medium">{secretToDelete?.name}</span> will be permanently deleted. Any agent that references this variable will fail until a new secret with the same name is added.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteSecret} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete API Key</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this API key? Agents using this key will fall back to the platform key.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteApiKey} variant="destructive" className="rounded-sm">
                Delete Key
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default SettingsPage;

